// DigiLocker connector.
// Read-only OAuth 2.0 against https://api.digitallocker.gov.in/public/oauth2/1.
// In live mode the integrating app provides the partner client_id, redirect_uri,
// and a small backend proxy that holds the client_secret (the public web app
// must not see the secret). In test mode we emit realistic mocks.
//
// Spec reference: DigiLocker Partner API v3 + Issued-Docs API v2.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { devModeEnabled, err, fetchWithBudget, liveDisabled, nowIso, ok, shortHash } from './_types.ts';

export type DigilockerDocType =
  | 'PANCR'
  | 'AADHCR'
  | 'DRVLC'
  | 'VOTER'
  | 'VEHICLE'
  | 'MARKSHEET10'
  | 'MARKSHEET12'
  | 'INSURANCE'
  | 'OTHER';

export interface DigilockerDoc {
  readonly id: string;
  readonly type: DigilockerDocType;
  readonly title: string;
  readonly issuer: string;
  readonly issuedAt: string;
  readonly uriRefId: string;
  readonly mimeType: 'application/pdf' | 'application/xml';
  readonly sizeBytes: number;
}

export interface DigilockerProfile {
  readonly digilockerUserId: string;
  readonly nameFull: string;
  readonly gender: 'M' | 'F' | 'T';
  readonly dob: string;
  readonly mobileMasked: string;
  readonly aadhaarLast4: string;
  readonly issuedAt: string;
}

export interface DigilockerSnapshot {
  readonly profile: DigilockerProfile;
  readonly docs: readonly DigilockerDoc[];
  readonly fetchedAt: string;
}

export interface DigilockerConnectorStatus extends ConnectorStatus {
  readonly id: 'digilocker';
  readonly docsCount: number;
}

export interface DigilockerConfig {
  readonly clientId: string;
  readonly redirectUri: string;
  readonly proxyBase: string; // server side that holds client_secret
}

interface DigilockerPersist {
  readonly connectedAt: string;
  readonly userIdHash: string;
  readonly docsCount: number;
  readonly profile: DigilockerProfile;
  readonly docs: readonly DigilockerDoc[];
}

const STORE_KEY = 'empire/connectors/digilocker';

export class DigilockerAdapter
  implements ConnectorAdapter<DigilockerConnectorStatus, DigilockerSnapshot>
{
  readonly id = 'digilocker';
  readonly displayName = 'DigiLocker';
  readonly displayNameHi = 'डिजिलॉकर';
  readonly dpdpCategory = 'identity-govt-issued-docs';
  readonly mode: ConnectorMode;
  readonly config: DigilockerConfig | null;

  constructor(mode: ConnectorMode = 'test', config: DigilockerConfig | null = null) {
    this.mode = mode;
    this.config = config;
  }

  async getStatus(): Promise<DigilockerConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.userIdHash ?? null,
      docsCount: persisted?.docsCount ?? 0,
      notes: this.mode === 'live'
        ? 'Live OAuth via partner client. Tokens stay in proxyBase.'
        : 'Test mode — realistic mocks for PAN, Driving Licence, Vehicle RC, marksheets.',
    };
  }

  async connect(): Promise<ConnectorResult<DigilockerConnectorStatus>> {
    if (this.mode === 'live') {
      if (!devModeEnabled()) return err(liveDisabled(this.id));
      if (!this.config) {
        return err({ code: 'not-configured', message: 'DigiLocker partner config missing.', retriable: false });
      }
      const url = new URL('https://api.digitallocker.gov.in/public/oauth2/1/authorize');
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('client_id', this.config.clientId);
      url.searchParams.set('redirect_uri', this.config.redirectUri);
      url.searchParams.set('state', crypto.randomUUID());
      url.searchParams.set('code_challenge_method', 'S256');
      url.searchParams.set('code_challenge', await pkceChallenge());
      window.location.assign(url.toString());
      return ok(await this.getStatus());
    }
    const mock = this.mockPayload();
    await this.#persist(mock);
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  async importPayload(): Promise<ConnectorResult<DigilockerSnapshot>> {
    if (this.mode === 'live') {
      if (!devModeEnabled()) return err(liveDisabled(this.id));
      if (!this.config) return err({ code: 'not-configured', message: 'DigiLocker config missing.', retriable: false });
      try {
        const resProfile = await fetchWithBudget(
          `${this.config.proxyBase}/digilocker/profile`,
          { method: 'GET', credentials: 'include' },
          { retries: 1, timeoutMs: 15_000 }
        );
        const resDocs = await fetchWithBudget(
          `${this.config.proxyBase}/digilocker/docs`,
          { method: 'GET', credentials: 'include' },
          { retries: 1, timeoutMs: 20_000 }
        );
        if (!resProfile.ok || !resDocs.ok) {
          return err({ code: 'network', message: `proxy ${resProfile.status}/${resDocs.status}`, retriable: true });
        }
        const profile = (await resProfile.json()) as DigilockerProfile;
        const docs = (await resDocs.json()) as DigilockerDoc[];
        const snap: DigilockerSnapshot = { profile, docs, fetchedAt: nowIso() };
        await this.#persist(snap);
        return ok(snap);
      } catch (e) {
        return err({ code: 'network', message: e instanceof Error ? e.message : 'proxy-error', retriable: true });
      }
    }
    const mock = this.mockPayload();
    await this.#persist(mock);
    return ok(mock);
  }

  mockPayload(): DigilockerSnapshot {
    const profile: DigilockerProfile = {
      digilockerUserId: 'demo-12345',
      nameFull: 'Aditya Sharma',
      gender: 'M',
      dob: '1992-04-15',
      mobileMasked: 'XXXXXX4321',
      aadhaarLast4: '4321',
      issuedAt: nowIso(),
    };
    const docs: DigilockerDoc[] = [
      { id: 'd-pan-01', type: 'PANCR', title: 'Permanent Account Number Card', issuer: 'Income Tax Dept', issuedAt: '2010-09-01', uriRefId: 'in.gov.itd-ABCDE1234F', mimeType: 'application/pdf', sizeBytes: 188_512 },
      { id: 'd-dl-01', type: 'DRVLC', title: 'Driving Licence', issuer: 'Delhi RTO', issuedAt: '2018-03-12', uriRefId: 'in.gov.delhi-DL0420180042312', mimeType: 'application/pdf', sizeBytes: 412_004 },
      { id: 'd-rc-01', type: 'VEHICLE', title: 'Vehicle Registration · DL3CBA1142', issuer: 'Delhi RTO', issuedAt: '2021-07-04', uriRefId: 'in.gov.delhi-DL3CBA1142', mimeType: 'application/pdf', sizeBytes: 305_240 },
      { id: 'd-v-01', type: 'VOTER', title: 'Voter ID', issuer: 'ECI', issuedAt: '2014-01-22', uriRefId: 'in.gov.eci-XYZ0123456', mimeType: 'application/pdf', sizeBytes: 250_310 },
      { id: 'd-m10-01', type: 'MARKSHEET10', title: 'CBSE Class X Marksheet', issuer: 'CBSE', issuedAt: '2008-05-30', uriRefId: 'in.gov.cbse-X-2008-A0042', mimeType: 'application/pdf', sizeBytes: 612_188 },
      { id: 'd-m12-01', type: 'MARKSHEET12', title: 'CBSE Class XII Marksheet', issuer: 'CBSE', issuedAt: '2010-06-04', uriRefId: 'in.gov.cbse-XII-2010-A0042', mimeType: 'application/pdf', sizeBytes: 624_004 },
    ];
    return { profile, docs, fetchedAt: nowIso() };
  }

  async #persist(snap: DigilockerSnapshot): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const record: DigilockerPersist = {
      connectedAt: snap.fetchedAt,
      userIdHash: await shortHash(snap.profile.digilockerUserId),
      docsCount: snap.docs.length,
      profile: snap.profile,
      docs: snap.docs,
    };
    localStorage.setItem(STORE_KEY, JSON.stringify(record));
  }
}

function readPersist(): DigilockerPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DigilockerPersist;
  } catch {
    return null;
  }
}

async function pkceChallenge(): Promise<string> {
  const verifier = crypto.randomUUID() + crypto.randomUUID();
  sessionStorage.setItem('empire/connectors/digilocker/pkce', verifier);
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

// Mapping helper for Wasiyat: turn a DigiLocker doc into a Wasiyat document
// category + suggested asset, so the integrating app can auto-create rows.

export const DIGILOCKER_TO_WASIYAT: Record<DigilockerDocType, {
  documentCategory: 'aadhaar' | 'pan' | 'passport' | 'driving_licence' | 'property_deed' | 'insurance_policy' | 'other';
  suggestedAssetCategory: 'bank_account' | 'mutual_fund' | 'demat' | 'fixed_deposit' | 'crypto' | 'insurance_life' | 'insurance_health' | 'insurance_motor' | 'property' | 'locker' | 'jewellery' | 'business_share' | 'pf_epfo' | 'nps' | 'ppf' | 'digital_domain' | 'digital_hosting' | 'digital_saas' | 'other' | null;
}> = {
  PANCR: { documentCategory: 'pan', suggestedAssetCategory: null },
  AADHCR: { documentCategory: 'aadhaar', suggestedAssetCategory: null },
  DRVLC: { documentCategory: 'driving_licence', suggestedAssetCategory: null },
  VOTER: { documentCategory: 'other', suggestedAssetCategory: null },
  VEHICLE: { documentCategory: 'other', suggestedAssetCategory: 'insurance_motor' },
  MARKSHEET10: { documentCategory: 'other', suggestedAssetCategory: null },
  MARKSHEET12: { documentCategory: 'other', suggestedAssetCategory: null },
  INSURANCE: { documentCategory: 'insurance_policy', suggestedAssetCategory: 'insurance_life' },
  OTHER: { documentCategory: 'other', suggestedAssetCategory: null },
};

export function makeDigilockerConnector(mode: ConnectorMode, config: DigilockerConfig | null = null): DigilockerAdapter {
  return new DigilockerAdapter(mode, config);
}
